import { Option } from '@/constants/general';
import {
    buildAccountBasedTableRows,
    buildCurrencyBasedTableRows,
    portalBalancesColumns
} from '@/helpers/portalBalancesHelper';
import { Modify } from '@/model/common';
import { useAppSelector } from '@/state/hooks';
import { selectCurrentAccount } from '@/state/reducers/authSlice';
import { BalancesSummaryReturn } from '@/types/sso';
import { useDebouncedValue } from '@/utils/hooks/useDebouncedValue';
import useLanguage from '@/utils/hooks/useLanguage';
import { fuzzyGlobalFilter } from '@/utils/hooks/useOpenPositionTable';
import { useTrueDateFormatter } from '@/utils/hooks/useTrueDateFormatter';
import { defaultAccountTypeOption, useSso } from '@/utils/providers/SSOProvider';
import { RadioGroup } from '@headlessui/react';
import {
    ExpandedState,
    filterFns,
    getCoreRowModel,
    getExpandedRowModel,
    getFacetedMinMaxValues,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable
} from '@tanstack/react-table';
import cn from 'classnames';
import { memo, useEffect, useMemo, useState } from 'react';
import { useDateFormatter } from 'react-aria';
import { BsCurrencyExchange } from 'react-icons/bs';
import { MdOutlineAccountBalanceWallet } from 'react-icons/md';
import ExportData from '../common/ExportData';
import FilterSelect from '../inputs/FilterSelect';
import PortalVirtualizedTable from '../table/PortalVirtualizedTable';

export interface PortalBalancesRow {
    rowType: 'account' | 'currency' | 'valueDate';
    label: string;
    weight?: number;
    amount?: number;
    amountInBaseCurrency?: number;
    accountType: string;
    displayCurrency: string;
    accountName?: string;
    currencyName?: string;
    valueDate?: string;
    subRows?: PortalBalancesRow[];
}
export interface PortalBalancesTableProps {
    balances: BalancesSummaryReturn;
    baseCurrency: string;
    isHistoric?: boolean;
    showRefresh?: boolean;
    onDeposit?: (currency: string) => void;
    onWithdraw?: (currency: string) => void;
}

export type PositionFilter = 'Show All' | 'Hide Closed Positions';

export const defaultBalanceSummary = {
    balances: {
        accountBased: [],
        currencyBased: []
    },
    totalInBaseCurrency: 0,
    baseCurrency: ''
};

type BalanceViewOption = Modify<
    Option,
    {
        label: React.ReactNode;
    }
>;

const balanceViewOptions: BalanceViewOption[] = [
    {
        label: (
            <div className="flex items-center gap-1 justify-center">
                <MdOutlineAccountBalanceWallet />

                <span>Accounts</span>
            </div>
        ),
        value: 'Accounts',
        title: 'View balances by accounts'
    },
    {
        label: (
            <div className="flex items-center gap-1 justify-center">
                <BsCurrencyExchange />
                <span>Currency</span>
            </div>
        ),
        value: 'Currency',
        title: 'View balances by currency'
    }
];

const PortalBalancesTable = (props: PortalBalancesTableProps) => {
    const { foundEntity, summaryHistoricDate, accountType, setAccountType, isSpecificAccountSelected } = useSso();
    const { isHistoric = false, balances: balanceData, onDeposit, onWithdraw } = props;
    const { selectedBalancesDirection } = useSso();
    const lang = useLanguage();
    const currentAccount = useAppSelector(selectCurrentAccount);
    const formatDate = useTrueDateFormatter();
    const [expandedRows, setExpandedRows] = useState<ExpandedState>({});
    const [columnSizing, setColumnSizing] = useState<Record<string, number>>({});
    const [sorting, setSorting] = useState<SortingState>([]);
    const [globalFilter, setGlobalFilter] = useState<string>('');
    const dateFormatter = useDateFormatter({
        timeZone: 'UTC'
    });
    const [balanceCurrentView, setBalanceCurrentView] = useState<BalanceViewOption>(balanceViewOptions[0]);

    const { balances, totalInBaseCurrency, baseCurrency } = balanceData;
    const { accountBased, currencyBased } = balances;
    const [totalAmount, setTotalAmount] = useState<number>(totalInBaseCurrency);
    const isCryptoDepositWithdrawalEnabled = foundEntity?.attributes?.includes('DELIVERABLE');

    // Build table data based balances data on the current view and selected account type
    const tableData = useMemo(() => {
        if (!balanceData) return [];
        if (balanceCurrentView.value === 'Accounts') {
            if (!isSpecificAccountSelected) {
                setTotalAmount(totalInBaseCurrency);
                return buildAccountBasedTableRows(accountBased);
            } else {
                const filteredAccountBased = accountBased.filter((item) => item.accountType === accountType?.value);
                setTotalAmount(filteredAccountBased[0]?.totalAccountAmountInBaseCurrency ?? 0);
                return buildAccountBasedTableRows(filteredAccountBased)[0]?.subRows ?? [];
            }
        }

        setTotalAmount(totalInBaseCurrency);
        return buildCurrencyBasedTableRows(currencyBased);
    }, [accountBased, currencyBased, balanceCurrentView, accountType]);

    // Dropdown options for account selection
    const accountDropdownOptions: Option[] = useMemo(() => {
        const accounts = accountBased.map((item) => {
            return {
                label: item.accountName,
                value: item.accountType
            };
        });

        return [defaultAccountTypeOption, ...accounts];
    }, [accountBased]);

    const shouldShowWeights = useMemo(() => {
        // If non GB10 or not a specific account, hide the weight column
        if (
            (accountType?.value !== 'GB10' && accountType?.value !== 'ManagedPortfolio') ||
            !isSpecificAccountSelected
        ) {
            return false;
        }

        const account = accountBased.find((item) => item.accountName === accountType.value);

        // Show the column only if all weights are present
        return !account?.subRows?.some((item) => item.weight == null);
    }, [accountBased, accountType, isSpecificAccountSelected]);

    // Columns for React Table
    const columns = useMemo(() => {
        return portalBalancesColumns({
            lang,
            formatDate,
            baseCurrency,
            totalInBaseCurrency: totalAmount,
            isCryptoDepositWithdrawalEnabled,
            onDeposit,
            onWithdraw,
            balanceCurrentView: balanceCurrentView.value,
            isSpecificAccountSelected,
            shouldShowWeights
        });
    }, [
        lang,
        formatDate,
        baseCurrency,
        balances,
        totalAmount,
        isCryptoDepositWithdrawalEnabled,
        balanceCurrentView,
        isSpecificAccountSelected,
        onDeposit,
        onWithdraw,
        selectedBalancesDirection,
        accountType
    ]);

    // Account View Export Data
    const accountExportData = useMemo(() => {
        if (!balanceData) return { headerData: [], data: [] };

        const isPortfolio = ['GB10', 'ManagedPortfolio'].includes(accountType?.value);

        // 1. Build the correct headers dynamic base setup
        let headers = ['Account', 'Currency', 'Value Date', 'Balance', `Balance (${baseCurrency})`];

        if (isSpecificAccountSelected) {
            headers = headers.filter((h) => h !== 'Account');
            if (isPortfolio) {
                headers.push('Weight (%)'); // Dynamically appends Weight only for GB10 view
            }
        }

        if (isHistoric) {
            headers = headers.filter((h) => h !== 'Value Date');
        }

        const data: (string | number | null)[][] = [];

        if (isSpecificAccountSelected) {
            const filteredBalance = balanceData.balances.accountBased.filter(
                (account) => account.accountName === accountType?.value
            );

            // Check if we actually found a matching account to prevent runtime errors
            if (filteredBalance.length > 0) {
                filteredBalance[0].subRows.forEach((currency) => {
                    if (isHistoric) {
                        data.push([
                            currency.currencyName,
                            currency.totalCurrencyAmount ?? 'N/A',
                            currency.totalCurrencyAmountInBaseCurrency ?? 'N/A',
                            ...(isPortfolio ? [currency.weight ?? 'N/A'] : []) // Push weight column mapping conditionally
                        ]);
                    } else {
                        currency.subRows.forEach((valueDate) => {
                            data.push([
                                currency.currencyName,
                                valueDate.valueDate,
                                valueDate.currencyAmount ?? 'N/A',
                                valueDate.currencyAmountInBaseCurrency ?? 'N/A',
                                ...(isPortfolio ? [valueDate.weight ?? 'N/A'] : []) // Push weight column mapping conditionally
                            ]);
                        });
                    }
                });
            }
        } else {
            // Standard view (Weight is omitted entirely from non-specific accounts)
            balanceData.balances.accountBased.forEach((account) => {
                account.subRows.forEach((currency) => {
                    if (isHistoric) {
                        data.push([
                            account.accountName,
                            currency.currencyName,
                            currency.totalCurrencyAmount ?? 'N/A',
                            currency.totalCurrencyAmountInBaseCurrency ?? 'N/A'
                        ]);
                    } else {
                        currency.subRows.forEach((valueDate) => {
                            data.push([
                                account.accountName,
                                currency.currencyName,
                                valueDate.valueDate,
                                valueDate.currencyAmount ?? 'N/A',
                                valueDate.currencyAmountInBaseCurrency ?? 'N/A'
                            ]);
                        });
                    }
                });
            });
        }

        return {
            headerData: headers,
            data
        };
        // Cleaned up dependency array (removed duplicate accountType)
    }, [balanceData, baseCurrency, accountType, isHistoric, isSpecificAccountSelected]);

    // Currency View Export Data
    const currencyExportData = useMemo(() => {
        if (!balanceData) return { headerData: [], data: [] };

        const headerData = ['Currency', 'Account', 'Value Date', 'Balance', `Balance (${baseCurrency})`];

        if (isHistoric) {
            headerData.splice(2, 1); // Remove 'Value Date' column if isHistoric is present
        }

        const data: (string | number | null)[][] = [];

        balanceData.balances.currencyBased.forEach((currency) => {
            currency.subRows.forEach((account) => {
                if (isHistoric) {
                    data.push([
                        currency.currencyName,
                        account.accountName,
                        account.totalAccountAmount === null ? 'N/A' : account.totalAccountAmount,
                        account?.totalAccountAmountInBaseCurrency === null
                            ? 'N/A'
                            : account?.totalAccountAmountInBaseCurrency
                    ]);
                } else {
                    account.subRows.forEach((valueDate) => {
                        data.push([
                            currency.currencyName,
                            account.accountName,
                            valueDate.valueDate,
                            valueDate.accountAmount,
                            valueDate.accountAmountInBaseCurrency === null
                                ? 'N/A'
                                : valueDate.accountAmountInBaseCurrency
                        ]);
                    });
                }
            });
        });

        return {
            headerData,
            data
        };
    }, [balanceData, baseCurrency, isHistoric]);

    // Export Data and File Name
    const { exportData, fileName } = useMemo(() => {
        return {
            exportData: balanceCurrentView.value === 'Accounts' ? accountExportData : currencyExportData,
            fileName: (() => {
                const pad = (n: number) => String(n).padStart(2, '0');
                const formatDate = (date: Date, includeTime: boolean): string => {
                    const y = date.getUTCFullYear();
                    const m = pad(date.getUTCMonth() + 1);
                    const d = pad(date.getUTCDate());
                    const datePart = `${y}-${m}-${d}`;
                    if (!includeTime) return datePart;
                    const h = pad(date.getUTCHours());
                    const min = pad(date.getUTCMinutes());
                    const s = pad(date.getUTCSeconds());
                    return `${datePart} ${h}h${min}m${s}s UTC`;
                };
                return [
                    isSpecificAccountSelected ? accountType.label : 'All',
                    balanceCurrentView.value,
                    'Balances',
                    foundEntity?.entityName,
                    isHistoric && summaryHistoricDate
                        ? formatDate(new Date(summaryHistoricDate), false)
                        : formatDate(new Date(), true)
                ]
                    .filter(Boolean)
                    .join(' ');
            })()
        };
    }, [
        balanceCurrentView,
        accountExportData,
        currencyExportData,
        accountType,
        foundEntity,
        isHistoric,
        summaryHistoricDate,
        dateFormatter
    ]);

    // React Table Instance
    const tableInstance = useReactTable({
        data: tableData,
        columns,
        state: { expanded: expandedRows, globalFilter, columnSizing, sorting },
        defaultColumn: { filterFn: filterFns.includesString },
        enableColumnResizing: true,
        columnResizeMode: 'onChange',
        globalFilterFn: (row, columnId, filterValue) => {
            return fuzzyGlobalFilter(row, columnId, filterValue, dateFormatter);
        },
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
        onExpandedChange: setExpandedRows,
        onColumnSizingChange: setColumnSizing,
        onSortingChange: setSorting,
        getExpandedRowModel: getExpandedRowModel(),
        getSubRows: (row: any) => row.subRows,
        enableRowSelection: true
    });

    const [debouncedTableInstance] = useDebouncedValue(tableInstance, 500);

    useEffect(() => {
        setAccountType(defaultAccountTypeOption);
    }, []);

    return (
        <div className="flex flex-col gap-1 bg-brand-background-dark p-4 sm:p-6 py-4 border border-brand-neutral-700 text-brand-text rounded-md overflow-hidden max-h-[750px]">
            <div className="flex flex-col h-full gap-4">
                <div className="flex items-center flex-wrap gap-3">
                    <div className="flex self-start pr-3 h-max">
                        {isHistoric ? lang.portal.historicBalances : lang.portal.currentBalances}
                    </div>

                    <div className="flex justify-start md:justify-end flex-wrap sm:flex-nowrap gap-2 sm:flex-1">
                        {!isSpecificAccountSelected && (
                            <div className="flex justify-end">
                                <RadioGroup
                                    value={balanceCurrentView.value}
                                    onChange={(filter) => {
                                        const selectedOption = balanceViewOptions.find((o) => o.value === filter);
                                        if (selectedOption) setBalanceCurrentView(selectedOption);
                                    }}
                                    className="flex bg-background border border-brand-neutral-700 rounded-sm p-0.5 gap-1 w-full xs:w-max">
                                    {balanceViewOptions.map((option, index) => (
                                        <RadioGroup.Option
                                            key={option.value}
                                            value={option.value}
                                            className="flex-1 md:flex-none">
                                            {({ checked }) => (
                                                <button
                                                    className={cn(
                                                        'px-4 py-1.5 text-xs font-medium w-full text-center focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary',
                                                        {
                                                            'bg-brand-primary shadow-sm text-brand-text': checked,
                                                            'text-brand-neutral-300 hover:text-brand-text': !checked,
                                                            'rounded-l-sm': index === 0,
                                                            'rounded-r-sm': index === balanceViewOptions.length - 1
                                                        }
                                                    )}>
                                                    {option.label}
                                                </button>
                                            )}
                                        </RadioGroup.Option>
                                    ))}
                                </RadioGroup>
                            </div>
                        )}
                        <div className="flex gap-2 flex-wrap w-full sm:w-auto sm:flex-nowrap">
                            <FilterSelect
                                selected={accountType}
                                options={accountDropdownOptions}
                                onChange={(selectedAccountType) => {
                                    setAccountType(selectedAccountType);
                                    setBalanceCurrentView(balanceViewOptions[0]);
                                }}
                                className="!text-xs md:!text-xs !rounded-sm w-auto"
                            />

                            <ExportData
                                fileName={fileName}
                                excelData={{
                                    data: exportData.data,
                                    fileName,
                                    headerData: exportData.headerData
                                }}
                                className="rounded-sm"
                            />
                        </div>
                    </div>
                </div>
                <MemoizedPortalBalancesTable
                    balances={tableData}
                    expandedRows={expandedRows}
                    globalFilter={globalFilter}
                    tableInstance={debouncedTableInstance}
                    columnSizing={columnSizing}
                    sorting={sorting}
                    currentAccount={currentAccount}
                />
            </div>
        </div>
    );
};

export default PortalBalancesTable;

const MemoizedPortalBalancesTable = memo(({ tableInstance }: any) => {
    // globalFilterEnabled
    return <PortalVirtualizedTable tableInstance={tableInstance} isLoadingTableContent={false} />;
});

MemoizedPortalBalancesTable.displayName = 'PortalBalancesTable';
